import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { FormSchema, FormSection } from '../models/form-schema.model';
import { FieldConfig } from '../models/field-config.model';
import { ConditionalRule, ConditionOperator } from '../models/condition.model';

@Injectable({ providedIn: 'root' })
export class ConditionEngineService {

  // mapa reactivo: fieldId → hidden/disabled en tiempo real
  private fieldStates = new Map<string, { hidden: boolean; disabled: boolean }>();

  apply(
    schema: FormSchema,
    form: FormGroup,
    destroy$: Subject<void>
  ): void {
    // evaluar estado inicial
    this.evaluate(schema, form);

    // re-evaluar en cada cambio del form
    form.valueChanges
      .pipe(takeUntil(destroy$))
      .subscribe(() => this.evaluate(schema, form));
  }

  getState(fieldId: string): { hidden: boolean; disabled: boolean } {
    return this.fieldStates.get(fieldId) ?? { hidden: false, disabled: false };
  }

  private evaluate(schema: FormSchema, form: FormGroup): void {
    const allFields = schema.sections.flatMap(s => s.fields);

    for (const field of allFields) {
      let hidden   = field.hidden   ?? false;
      let disabled = field.disabled ?? false;

      if (!field.conditions?.length) {
        this.fieldStates.set(field.id, { hidden, disabled });
        continue;
      }

      for (const rule of field.conditions) {
        const watchedValue = form.get(rule.when)?.value;
        const condMet      = this.evaluate_condition(watchedValue, rule);

        if (!condMet) continue;

        if (rule.action === 'show')    hidden   = false;
        if (rule.action === 'hide')    hidden   = true;
        if (rule.action === 'enable')  disabled = false;
        if (rule.action === 'disable') disabled = true;
      }

      this.fieldStates.set(field.id, { hidden, disabled });

      // sincronizar con el FormControl
      const ctrl = form.get(field.id);
      if (!ctrl) continue;

      if (disabled && ctrl.enabled)   ctrl.disable({ emitEvent: false });
      if (!disabled && ctrl.disabled) ctrl.enable({ emitEvent: false });

      // limpiar valor al ocultar
      if (hidden) ctrl.reset(null, { emitEvent: false });
    }
  }

  private evaluate_condition(
    watchedValue: unknown,
    rule: ConditionalRule
  ): boolean {
    const { operator, value } = rule;
    const isArray = Array.isArray(watchedValue);

    switch (operator as ConditionOperator) {
      case 'equals':
        return isArray
          ? (watchedValue as unknown[]).includes(value)
          : watchedValue === value;
      case 'notEquals':
        return isArray
          ? !(watchedValue as unknown[]).includes(value)
          : watchedValue !== value;
      case 'contains':
        return isArray
          ? (watchedValue as unknown[]).includes(value)
          : String(watchedValue ?? '').includes(String(value));
      case 'greaterThan':  return Number(watchedValue) > Number(value);
      case 'lessThan':     return Number(watchedValue) < Number(value);
      case 'isEmpty':
        return isArray
          ? (watchedValue as unknown[]).length === 0
          : !watchedValue || watchedValue === '';
      case 'isNotEmpty':
        return isArray
          ? (watchedValue as unknown[]).length > 0
          : !!watchedValue && watchedValue !== '';
      default:             return false;
    }
  }
}